import {
  Activity,
  Award,
  BarChart,
  Bookmark,
  Box,
  CheckCircle,
  Clock,
  Cog,
  DollarSign,
  Eye,
  Flag,
  Gauge,
  GitMerge,
  Heart,
  Layers,
  LineChart,
  Mail,
  Map,
  PieChart,
  Send,
  Server,
  Shield,
  Sliders,
  Star,
  Target,
  Trophy,
  Users,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils.js';

/**
 * KPI type/metric icon chip. Renders a coloured circle holding the
 * lucide glyph the operator picked when creating the KPI. Mirrors
 * `wf-audit/src/slices/setup/theme/components/ui/icon.tsx`'s string-
 * keyed `IconMap` — narrowed here to the ~30 icons the legacy KPI
 * picker actually offers. Anything outside the map falls back to a
 * `BarChart` glyph.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  activity: Activity,
  award: Award,
  'bar-chart': BarChart,
  bookmark: Bookmark,
  box: Box,
  'check-circle': CheckCircle,
  clock: Clock,
  cog: Cog,
  'dollar-sign': DollarSign,
  eye: Eye,
  flag: Flag,
  gauge: Gauge,
  'git-merge': GitMerge,
  heart: Heart,
  layers: Layers,
  'line-chart': LineChart,
  mail: Mail,
  map: Map,
  'pie-chart': PieChart,
  send: Send,
  server: Server,
  shield: Shield,
  sliders: Sliders,
  star: Star,
  target: Target,
  trophy: Trophy,
  users: Users,
  wrench: Wrench,
  zap: Zap,
};

export function KpiChip({
  icon,
  color,
  size = 'md',
  className,
}: {
  icon: string;
  color: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}): JSX.Element {
  const Glyph = ICON_MAP[icon] ?? BarChart;
  const sizes: Record<typeof size, { box: string; glyph: string }> = {
    sm: { box: 'h-6 w-6', glyph: 'h-3.5 w-3.5' },
    md: { box: 'h-9 w-9', glyph: 'h-5 w-5' },
    lg: { box: 'h-12 w-12', glyph: 'h-6 w-6' },
  };
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-foreground/15',
        sizes[size].box,
        className,
      )}
      style={{ backgroundColor: `${color}33`, color }}
    >
      <Glyph className={sizes[size].glyph} />
    </span>
  );
}
